import {
  Headphones,
  TrendingUp,
  Settings,
  Search,
  PenTool,
  Server,
} from "lucide-react"

interface UseCase {
  icon: React.ReactNode
  title: string
  description: string
  agents: string[]
}

const useCases: UseCase[] = [
  {
    icon: <Headphones className="h-6 w-6" />,
    title: "Customer Support Team",
    description: "Handle inquiries, resolve issues, and escalate complex cases automatically.",
    agents: ["Support Agent", "FAQ Bot", "Escalation Handler"],
  },
  {
    icon: <TrendingUp className="h-6 w-6" />,
    title: "Sales & Marketing Team",
    description: "Generate leads, nurture prospects, and create compelling marketing content.",
    agents: ["Sales Assistant", "Content Writer", "Social Manager"],
  },
  {
    icon: <Settings className="h-6 w-6" />,
    title: "Operations Team",
    description: "Streamline internal processes, track expenses, and manage HR requests.",
    agents: ["Expense Bot", "HR Assistant", "IT Support"],
  },
  {
    icon: <Search className="h-6 w-6" />,
    title: "Research & Analysis Team",
    description: "Gather market intelligence, analyze data, and generate comprehensive reports.",
    agents: ["Research Bot", "Data Analyst", "Report Generator"],
  },
  {
    icon: <PenTool className="h-6 w-6" />,
    title: "Content Creation Team",
    description: "Produce blog posts, optimize for SEO, and maintain brand consistency.",
    agents: ["Blog Writer", "SEO Optimizer", "Editor"],
  },
  {
    icon: <Server className="h-6 w-6" />,
    title: "IT & DevOps Team",
    description: "Assist with code reviews, automate deployments, and monitor systems.",
    agents: ["Code Assistant", "Deployment Bot", "Monitoring Agent"],
  },
]

/**
 * Use cases section displaying common AI team configurations.
 * Shows a grid of department-based use cases with their agent composition.
 */
export function UseCasesSection() {
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-3xl font-bold text-neutral-900 lg:text-4xl">
            AI Teams for Every Department
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-neutral-600">
            Build specialized AI teams tailored to your department's needs. From customer
            support to engineering, there's an AI team for every function.
          </p>
        </div>

        {/* Use Cases Grid */}
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {useCases.map((useCase, index) => (
            <div
              key={index}
              className="group rounded-xl border border-neutral-200 bg-white p-6 transition-all hover:border-primary/30 hover:shadow-lg"
            >
              {/* Icon */}
              <div className="mb-4 inline-flex rounded-xl bg-primary/10 p-3 text-primary transition-colors group-hover:bg-primary group-hover:text-white">
                {useCase.icon}
              </div>

              {/* Title */}
              <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                {useCase.title}
              </h3>

              {/* Description */}
              <p className="mb-4 text-sm text-neutral-600">{useCase.description}</p>

              {/* Agents */}
              <div className="flex flex-wrap gap-2">
                {useCase.agents.map((agent, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-neutral-100 px-2.5 py-1 text-xs text-neutral-600"
                  >
                    {agent}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
